import { createContext, useContext, useState, useEffect } from 'react';
import { getProductFromURL, getProductByParam } from '../config/products';

const SurveyContext = createContext();

export function useSurvey() {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
}

export function SurveyProvider({ children }) {
  const [product, setProduct] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [surveyComplete, setSurveyComplete] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // Initialize product from URL
  useEffect(() => {
    const productParam = getProductFromURL();
    const productConfig = getProductByParam(productParam);
    setProduct(productConfig);

    // Load saved state from localStorage
    const savedState = localStorage.getItem('renuvia-survey-state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.sessionId) setSessionId(parsed.sessionId);
        if (parsed.userId) setUserId(parsed.userId);
        if (parsed.answers) setAnswers(parsed.answers);
        if (parsed.currentPage) setCurrentPage(parsed.currentPage);
      } catch (e) {
        console.error('Failed to load saved state:', e);
      }
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (sessionId || Object.keys(answers).length > 0) {
      const stateToSave = {
        sessionId,
        userId,
        answers,
        currentPage,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('renuvia-survey-state', JSON.stringify(stateToSave));
    }
  }, [sessionId, userId, answers, currentPage]);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value
    }));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => prev + 1);
    window.scrollTo(0, 0);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
    window.scrollTo(0, 0);
  };

  const clearSurvey = () => {
    setAnswers({});
    setCurrentPage(1);
    setSessionId(null);
    setUserId(null);
    setSurveyComplete(false);
    setCheckoutUrl(null);
    localStorage.removeItem('renuvia-survey-state');
  };

  const value = {
    product,
    sessionId,
    setSessionId,
    userId,
    setUserId,
    answers,
    updateAnswer,
    currentPage,
    setCurrentPage,
    goToNextPage,
    goToPreviousPage,
    isLoading,
    setIsLoading,
    error,
    setError,
    surveyComplete,
    setSurveyComplete,
    checkoutUrl,
    setCheckoutUrl,
    clearSurvey
  };

  return (
    <SurveyContext.Provider value={value}>
      {children}
    </SurveyContext.Provider>
  );
}
